import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core'
import { FlipHeaderInfo } from '../../../models/flip/flip-header-info'
import { ClientScore } from '../../../models/client/client-score-dto'
import { FlipScores } from '../../../models/flip/flip-score-dto'
import { FlipGraphScores } from '../../../models/flip/flip-graph-scores'
import { FlipGraphSubScores } from '../../../models/flip/flip-graph-sub-scores'
import {
  ClientService,
  DetailsAndScoreData,
} from '../../../services/client/client.service'
import { DetailsAndScoreResponse } from '../../../models/flip/details-scores-response'
import {
  interval,
  ObservableInput,
  Subscription,
  switchMap,
  throttleTime,
} from 'rxjs'
import { CommonService } from '../../../services/common/common.service'
import { distinctUntilChanged } from 'rxjs/operators'

@Component({
  selector: 'app-flip-dashboard-graph',
  templateUrl: './flip-dashboard-graph.component.html',
  styleUrl: './flip-dashboard-graph.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlipDashboardGraphComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() clientId!: string
  @Input() autoRefresh: boolean = false
  public detailsAndScore!: DetailsAndScoreResponse
  totalScore: number
  totalCount: number
  lastAssessmentDate: Date

  constructor(
    private clientService: ClientService,
    private cd: ChangeDetectorRef,
    public commonService: CommonService,
  ) {}

  ngOnInit() {
    this.processData()
  }

  initData(response: DetailsAndScoreData) {
    this.detailsAndScore = {
      flipGraphDatas:
        response.clientScore?.map((item) => new ClientScore(item)) ?? [],
      flipUpperAndLowerLimits:
        response.flipScores?.map((item) => new FlipScores(item)) ?? [],
      flipHeaderInfo: response.flipHeaderInfo ?? [],
      flipScores:
        response.flipGraphScores?.map((item) => new FlipGraphScores(item)) ??
        [],
      flipSubScores:
        response.flipGraphSubScores?.map(
          (item) => new FlipGraphSubScores(item),
        ) ?? [],
    } as DetailsAndScoreResponse

    this.totalScore = this.detailsAndScore?.flipHeaderInfo?.totalScore
    this.totalCount = this.detailsAndScore?.flipHeaderInfo?.totalCount
    this.lastAssessmentDate =
      this.detailsAndScore?.flipHeaderInfo?.lastAssessmentDate

    this.cd.detectChanges()
  }

  processData() {
    if (this.clientId) {
      this.clientService.DetailsAndScore(this.clientId).subscribe({
        next: (response) => {
          if (!response) {
            console.error('Service response is undefined.')
            return {
              flipGraphDatas: [],
              flipUpperAndLowerLimits: [],
              flipHeaderInfo: FlipHeaderInfo,
              flipScores: [],
              flipSubScores: [],
            } // Provide default empty arrays
          }

          this.initData(response)
          return this.detailsAndScore // Cast to DetailsAndScoreResponse
        },
        error: (err) => {
          console.error('Error fetching details:', err)
        },
        complete: () => {},
      })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If autoRefresh flag changes to true, start interval
    if (changes['autoRefresh'] && this.autoRefresh) {
      this.startAutoRefresh()
    }

    // If autoRefresh becomes false, stop any existing refresh
    if (changes['autoRefresh'] && !this.autoRefresh) {
      this.stopAutoRefresh()
    }

    this.cd.markForCheck()
  }

  private refreshSub?: Subscription

  private startAutoRefresh(): void {
    // Stop any previous interval before starting new one
    this.stopAutoRefresh()

    this.refreshSub = interval(this.commonService.getRefreshTimeOut())
      .pipe(
        throttleTime(this.commonService.getThrottleTimeOut()),
        switchMap(
          (
            value: number,
            index: number,
          ): ObservableInput<DetailsAndScoreData> => {
            if (this.clientId) {
              return this.clientService.DetailsAndScore(this.clientId)
            }
            return null
          },
        ),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((response) => this.initData(response as DetailsAndScoreData))
  }

  private stopAutoRefresh(): void {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe()
      this.refreshSub = undefined
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh()
  }
}
